/**
 * TransferProjectModal — move a block to another job.
 *
 * The dialog for the thing the PM already does with a red pen: a foreman's
 * daily prints one job's number and code, the work is deliberately billed under
 * another job. Reachable from a PENDING or SKIPPED block card in the PDF Inbox.
 * A SAVED block is not offered it — it is in a rollup, and Re-open is the door.
 *
 * THE CONSEQUENCE COPY IS THE POINT — but it is standing explanation, not a
 * per-move decision, so it lives on the title's "?" rather than in the form.
 * Everything this does to money happens out of sight (rates re-derive against
 * the other job's roster, unmatched rows drop to unknown), and the tip states
 * all of it in PM English, one hover away, with the topic behind it. What the
 * form itself carries is what CHANGES per move: the two destination pickers,
 * the pre-flight refusals, and the reason. Sibling of MoveBlockModal in shape
 * and vocabulary; deliberately NOT merged with it, because that one moves SAVED
 * work between PCOs on ONE job and every guard it carries is about weeks and
 * rollups.
 */
import { useEffect, useMemo, useState } from 'react';
import type { InboxBlock, SettingsPco, TransferBlockResolution } from '@coffman-tm/shared';
import { api, ApiCallError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { formatPcoNumber } from '../../lib/format';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { Dropdown } from '../design';
import { HelpTip } from '../HelpTip';

const REASON_MIN = 10;
const REASON_MAX = 280;

/**
 * PM English for every refusal this dialog can receive.
 *
 * A bare `project_forbidden` rendered in red next to an empty dropdown is what
 * the client actually saw on 2026-08-03 — a code, not a sentence, describing a
 * bug rather than anything he did. The server sends prose for most of these;
 * this is the floor for the ones it doesn't, and the guarantee that a raw code
 * never reaches the screen.
 */
export function transferErrorCopy(payload: { error?: string; message?: string }): string {
  if (payload.message && payload.message.trim() !== '') return payload.message;
  switch (payload.error) {
    case 'project_forbidden':
      return 'You don’t have access to that job — ask an administrator to add you, then try again.';
    case 'invalid_destination':
      return 'That PCO isn’t available on the job you picked. Choose another.';
    case 'same_project':
      return 'That block is already on this job.';
    case 'block_is_saved':
      return 'This block is already saved into a rollup. Re-open it first, then move it.';
    case 'block_ignored':
      return 'This block’s activity code is on the ignore list. Un-ignore the code first.';
    case 'block_has_materials':
      return 'Remove the hand-added material lines first — they’re priced against this job’s list.';
    case 'block_has_manual_lines':
      return 'Remove the hand-added labor/equipment lines first — their hours wouldn’t survive the move.';
    case 'twin_exists_on_target':
      return 'That job already has a block for this day, foreman and code. Resolve it there first so the work can’t bill twice.';
    case 'manual_block_not_transferable':
      return 'This block was added by hand, not parsed from a PDF — add it on the other job instead.';
    case 'destination_code_ignored':
      return 'That activity code is on the other job’s ignore list. Un-ignore it there first.';
    case 'sibling_mint_conflict':
      return 'Another move of this same report is in flight. Give it a moment and try again.';
    case 'audit_write_failed':
      return 'Could not record this move, so nothing was moved. Try again.';
    case 'invalid_reason':
      return 'The reason needs to be between 10 and 280 characters.';
    default:
      return 'That move didn’t go through. Nothing was changed — try again, or contact support if it keeps happening.';
  }
}

export interface TransferProjectModalProps {
  open: boolean;
  block: InboxBlock | null;
  onCancel: () => void;
  /** Fired after a successful transfer — the block has LEFT this project. */
  onTransferred: (result: { projectLabel: string; resolution: TransferBlockResolution }) => void;
}

export function TransferProjectModal({
  open,
  block,
  onCancel,
  onTransferred,
}: TransferProjectModalProps) {
  const { token, me } = useAuth();
  const [projectId, setProjectId] = useState('');
  const [pcos, setPcos] = useState<SettingsPco[] | null>(null);
  const [pcoId, setPcoId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingPcos, setLoadingPcos] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setProjectId('');
    setPcos(null);
    setPcoId('');
    setReason('');
    setErr(null);
  }, [open, block?.daily_entry_id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  // FENCING, CLIENT SIDE: only jobs this account is a member of, minus the one
  // we're standing in. `me.projects` is the server's own membership list. The
  // API proves membership again on the destination — this is the affordance,
  // never the authority.
  const otherProjects = useMemo(
    () => (me?.projects ?? []).filter((p) => p.project_id !== me?.active_project_id),
    [me],
  );

  // The destination job's PCOs, read with a one-call project override.
  useEffect(() => {
    if (!open || !token || !projectId) {
      setPcos(null);
      setPcoId('');
      return;
    }
    let cancelled = false;
    setLoadingPcos(true);
    setErr(null);
    void (async () => {
      try {
        const list = await api.settings.pcosForProject(token, projectId);
        if (cancelled) return;
        setPcos(list);
      } catch (e) {
        if (cancelled) return;
        setPcos([]);
        setErr(
          e instanceof ApiCallError && e.payload && typeof e.payload === 'object'
            ? transferErrorCopy(e.payload as { error?: string; message?: string })
            : 'Could not load that job’s PCOs. Check your connection and try again.',
        );
      } finally {
        if (!cancelled) setLoadingPcos(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, projectId]);

  const projectOptions = useMemo(
    () => [
      { value: '', label: 'Select a job…' },
      ...otherProjects.map((p) => ({
        value: p.project_id,
        label: p.name ? `${p.project_number} — ${p.name}` : p.project_number,
      })),
    ],
    [otherProjects],
  );

  const usablePcos = useMemo(
    () =>
      (pcos ?? [])
        .filter((p) => p.status !== 'void')
        .sort((a, b) => a.pco_number.localeCompare(b.pco_number)),
    [pcos],
  );

  /**
   * THREE EXPLICIT STATES, never one silent one. An empty "Select a PCO…" with
   * nothing behind it is what the PM saw during the 2026-08-03 header bug and
   * it is unreadable — it looks identical whether the fetch is in flight, came
   * back empty, or failed.
   */
  const pcoPlaceholder =
    !projectId
      ? 'Pick a job first'
      : loadingPcos
        ? 'Loading…'
        : pcos === null
          ? 'Pick a job first'
          : usablePcos.length === 0
            ? 'No PCOs on that job'
            : 'Select a PCO…';

  const pcoOptions = useMemo(
    () => [
      { value: '', label: pcoPlaceholder },
      ...usablePcos.map((p) => ({
        value: p.id,
        label: `PCO ${formatPcoNumber(p.pco_number)} — ${p.description}`,
      })),
    ],
    [usablePcos, pcoPlaceholder],
  );

  const targetLabel = useMemo(() => {
    const p = otherProjects.find((x) => x.project_id === projectId);
    return p ? p.project_number : 'the other job';
  }, [otherProjects, projectId]);

  // PRE-FLIGHT the two refusals the card can already see, so the PM is told
  // BEFORE he fills in a reason instead of after he presses the button. The
  // server still enforces both — this is the affordance, not the gate.
  const billableMaterials = block
    ? block.materials.filter((m) => m.status === 'confirmed' || m.status === 'edited').length
    : 0;
  const manualLines = block
    ? block.labor.filter((r) => r.is_manual).length +
      block.equipment.filter((r) => r.is_manual).length
    : 0;
  const preflightBlock =
    manualLines > 0
      ? `This block has ${manualLines} hand-added labor/equipment ${manualLines === 1 ? 'line' : 'lines'}. Their hours are tied to this job’s rates and wouldn’t survive the move — remove them here, move the block, then add them back on the other job.`
      : billableMaterials > 0
        ? `This block has ${billableMaterials} hand-added material ${billableMaterials === 1 ? 'line' : 'lines'}. Materials are priced from a per-job list — remove them here, move the block, then add them back on the other job.`
        : null;

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const canSubmit =
    projectId !== '' &&
    pcoId !== '' &&
    reasonValid &&
    !busy &&
    !loadingPcos &&
    preflightBlock === null;

  if (!open || !block) return null;

  const handleConfirm = async () => {
    if (!token || !canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.blocks.transferProject(token, block.daily_entry_id, {
        destination_project_id: projectId,
        destination_pco_id: pcoId,
        reason: trimmedReason,
      });
      onTransferred({ projectLabel: targetLabel, resolution: res.resolution });
    } catch (e) {
      setErr(
        e instanceof ApiCallError && e.payload && typeof e.payload === 'object'
          ? transferErrorCopy(e.payload as { error?: string; message?: string })
          : 'That move didn’t go through. Nothing was changed — try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const hasOtherProjects = otherProjects.length > 0;

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Move this block to another job"
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="confirm-modal-head">
          <div
            className="confirm-modal-title"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Move to another job
            <HelpTip title="What the move changes" learnMore="moving-blocks">
              Everyone and every machine on this block is re-matched against{' '}
              <strong>{targetLabel}</strong>&rsquo;s roster: the hours and rates you see now
              are cleared and re-derived from that job, and anyone or anything it
              doesn&rsquo;t carry shows up as <em>unknown</em> for you to add there before
              the block can save. The original page and the code the foreman filed stay on
              record exactly as printed — copies sent to the GC show{' '}
              <strong>{targetLabel}</strong>&rsquo;s job banner with a note saying what the
              foreman filed. The block lands in <strong>{targetLabel}</strong>&rsquo;s PDF
              Inbox as pending, and you review and save it there like any other block.
            </HelpTip>
          </div>
        </div>
        <div className="confirm-modal-body">
          <div
            style={{
              background: 'var(--surface-warm, var(--surface))',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              padding: '8px 10px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                color: 'var(--ink-3)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Currently on job {me?.project.project_number ?? ''}
            </div>
            <div style={{ marginTop: 2 }}>
              <span className="mono">{block.activity_code}</span>
              {block.code_description ? ` — ${block.code_description}` : ''}
            </div>
            <div className="muted" style={{ marginTop: 2 }}>
              {block.labor.length} labor {block.labor.length === 1 ? 'line' : 'lines'} ·{' '}
              {block.equipment.length} equipment{' '}
              {block.equipment.length === 1 ? 'line' : 'lines'}
            </div>
          </div>

          {preflightBlock !== null && (
            <div
              style={{
                border: '1px solid var(--rule)',
                borderLeft: '3px solid var(--warning, #d18b1f)',
                borderRadius: 4,
                padding: '8px 10px',
                fontSize: 12.5,
                color: 'var(--ink-2)',
                marginBottom: 12,
              }}
            >
              {preflightBlock}
            </div>
          )}

          {!hasOtherProjects ? (
            <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              This account only has access to one job, so there is nowhere to move this block.
            </p>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Destination job
              </label>
              <Dropdown
                options={projectOptions}
                value={projectId}
                onChange={setProjectId}
                disabled={busy}
                style={{ width: '100%' }}
              />

              <label
                style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}
              >
                Destination PCO
              </label>
              <Dropdown
                options={pcoOptions}
                value={pcoId}
                onChange={setPcoId}
                disabled={busy || !projectId || loadingPcos}
                style={{ width: '100%' }}
              />

              <label
                style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}
              >
                Reason{' '}
                <span className="muted" style={{ fontWeight: 400 }}>
                  (required)
                </span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                rows={3}
                placeholder="Why is this work billing on the other job? (10–280 chars)"
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 2, textAlign: 'right' }}>
                {trimmedReason.length}/{REASON_MAX}
              </div>
            </>
          )}

          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-cta"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {busy ? 'Moving…' : 'Move block'}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
};
